import { createContext, useContext } from 'react';

import dayjs from 'dayjs';

/**
 * Context for communicating which assignment fragments have sticky labels active
 * (i.e., text fits within the bar). When a fragment's text overflows, its sticky
 * label is not rendered and the original ScrollableText should be shown instead.
 */
interface StickyLabelsContextValue {
  /** Map of assignment ID -> Set of fragment start dates (ISO strings) that have sticky labels */
  stickyFragmentsByAssignment: Map<number, Set<string>>;
}

const StickyLabelsContext = createContext<StickyLabelsContextValue>({
  stickyFragmentsByAssignment: new Map(),
});

export const StickyLabelsProvider = StickyLabelsContext.Provider;

/**
 * Returns true if the given assignment fragment has an active sticky label (text fits).
 * Checks by assignment ID and fragment start date.
 */
export const useStickyLabelActive = (
  assignmentId: number,
  fragmentStartDate: dayjs.Dayjs
): boolean => {
  const { stickyFragmentsByAssignment } = useContext(StickyLabelsContext);
  const fragmentDates = stickyFragmentsByAssignment.get(assignmentId);
  if (!fragmentDates) return false;
  return fragmentDates.has(fragmentStartDate.startOf('day').toISOString());
};
